import { config } from "../config";
import { ApiError } from "./errors";
import { callGateway, type CallerIdentity } from "./gateway";
import { AUTH_REQUEST_ID, normalizeIndianMobile } from "./login";
import { humanRefused, withHuman } from "../auth/human";

/**
 * Staff, operator and sub-operator passwords (owner 2026-10-22): CHANGE in Edit
 * profile, FORGOT on the sign-in screen. Subscriber passwords are elsewhere.
 *
 *   POST /access/operator/{ns}/account/password       {currentPassword, newPassword}
 *   POST /access/operator/{ns}/account/password/sms   {mobile, accessToken, newPassword}
 *   GET  /access/operator/{ns}/account/password/rules
 *   POST /access/auth/password/forgot/widget           {authRequestId, mobile, accessToken, username?}
 *   POST /access/auth/password/forgot/username         {authRequestId, ticket, username}
 *   POST /access/auth/password/forgot/email            {authRequestId, ticket, code}
 *   POST /access/auth/password/reset                   {authRequestId, resetTicket, newPassword}
 *
 * 🔑 NO USER ID TRAVELS on the change: radaccess acts on the token's subject.
 * The namespace in the path is the caller's OWN, only its containment.
 * Passwords go in JSON bodies over TLS and nowhere else: not into the URL,
 * storage, or any log line here.
 */

export type PasswordRules = {
  minLength: number;
  requiresUppercase: boolean;
  requiresLowercase: boolean;
  requiresNumber: boolean;
  requiresSymbol: boolean;
};

/** The rules as one line for under the field. */
export function describeRules(r: PasswordRules): string {
  const parts = [`at least ${r.minLength} characters`];
  if (r.requiresLowercase) parts.push("a lower-case letter");
  if (r.requiresUppercase) parts.push("an upper-case letter");
  if (r.requiresNumber) parts.push("a number");
  if (r.requiresSymbol) parts.push("a symbol");
  return parts.length === 1 ? parts[0]! : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/**
 * The first rule p breaks, in words, or null. Exactly ZITADEL's checks
 * (length in bytes, ASCII classes, a symbol is anything else), so the form
 * never refuses what the server would take, nor the reverse.
 */
export function passwordProblem(p: string, r: PasswordRules | null): string | null {
  if (!p) return "Enter a new password.";
  if (!r) return null;
  if (new TextEncoder().encode(p).length < r.minLength) return `Use at least ${r.minLength} characters.`;
  if (r.requiresLowercase && !/[a-z]/.test(p)) return "Add a lower-case letter.";
  if (r.requiresUppercase && !/[A-Z]/.test(p)) return "Add an upper-case letter.";
  if (r.requiresNumber && !/[0-9]/.test(p)) return "Add a number.";
  if (r.requiresSymbol && !/[^A-Za-z0-9]/.test(p)) return "Add a symbol.";
  return null;
}

export function isRules(v: unknown): v is PasswordRules {
  const r = v as PasswordRules | null;
  return (
    !!r && typeof r === "object" && typeof r.minLength === "number" && r.minLength > 0 &&
    ["requiresUppercase", "requiresLowercase", "requiresNumber", "requiresSymbol"].every((k) => typeof (r as Record<string, unknown>)[k] === "boolean")
  );
}

const accountPath = (namespace: string, leaf: string) => `/operator/${encodeURIComponent(namespace)}/account/${leaf}`;

export async function getPasswordRules(identity: CallerIdentity, namespace: string, signal?: AbortSignal): Promise<PasswordRules | null> {
  const r = await callGateway<unknown>(identity, config().gatewayBase, accountPath(namespace, "password/rules"), signal ? { signal } : {});
  return isRules(r.data) ? r.data : null;
}

export async function changePassword(identity: CallerIdentity, namespace: string, currentPassword: string, newPassword: string): Promise<void> {
  await callGateway(identity, config().gatewayBase, accountPath(namespace, "password"), {
    method: "POST",
    body: { currentPassword, newPassword },
  });
}

/**
 * Edit profile without the current password (owner 2026-10-23): an MSG91
 * widget token proving the caller's OWN verified mobile replaces it. Throws
 * before any call for a number that is not a 10-digit Indian mobile.
 */
export async function changePasswordBySms(identity: CallerIdentity, namespace: string, mobile: string, accessToken: string, newPassword: string): Promise<void> {
  const e164 = normalizeIndianMobile(mobile);
  if (e164 === null) throw new ApiError("bad-request", "Enter a 10-digit Indian mobile number.", 400, null, { error: "invalid_mobile" });
  await callGateway(identity, config().gatewayBase, accountPath(namespace, "password/sms"), {
    method: "POST",
    body: { mobile: e164, accessToken, newPassword },
  });
}

/**
 * The code for a password change, by SMS, email or both, to the caller's OWN
 * mobile and verified email (radaccess .../account/password/code, owner
 * 2026-10-25). Answers the request id and where it went; never the code.
 */
export async function sendAccountCode(
  identity: CallerIdentity,
  namespace: string,
  channels: ("sms" | "email")[],
  purpose: "change_password" | "link_account" | "radius_attributes" = "change_password",
): Promise<{ requestId: string; resendIn: number; sms: boolean; email?: string }> {
  const r = await callGateway<{ requestId: string; resendIn?: number; sent?: string[]; email?: string }>(
    identity,
    config().gatewayBase,
    accountPath(namespace, "password/code"),
    { method: "POST", body: { channels, purpose } },
  );
  const sent = r.data.sent ?? [];
  return {
    requestId: r.data.requestId,
    resendIn: r.data.resendIn ?? 30,
    sms: sent.includes("sms"),
    ...(r.data.email ? { email: r.data.email } : {}),
  };
}

/** What the person is told when a change or reset is refused. */
export function passwordErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const b = (err.body ?? {}) as { error?: unknown; problem?: unknown };
    if (err.status === 401 && b.error === "invalid_current_password") return "The current password is not right.";
    if (err.status === 422 && b.error === "password_unchanged") return "The new password is the same as the current one. Choose a different one.";
    if (err.status === 422 && b.error === "password_policy")
      return typeof b.problem === "string" && b.problem ? `Not accepted: ${b.problem}.` : "The new password does not meet the password rules.";
    if (err.status === 429) return "Too many attempts. Wait a few minutes and try again.";
    if (err.status === 423) return "This account is locked after too many attempts. Ask your administrator to unlock it.";
    if (err.status === 401 && b.error === "widget_token_rejected") return "That code is not right, or it was already used. Ask for a new code.";
    if (err.status === 403 && b.error === "mobile_not_yours") return "That is not the verified mobile number of this login. Enter the number you sign in with.";
    if (err.status === 409 && b.error === "no_mobile") return "This login has no mobile number. Ask your administrator to add it, or sign out and use “Forgot password?” with your email.";
    if (err.status === 502 && b.error === "mobile_verification_failed") return "Your mobile number could not be verified just now, so the password was not changed. Try again in a few minutes.";
    if (err.status === 400 && b.error === "invalid_mobile") return "Enter a 10-digit Indian mobile number.";
    if (err.status === 403) return "This login cannot change its password here.";
  }
  return "That could not be completed. Try again in a moment.";
}

// --- Forgotten password: the sign-in screen, no token yet -----------------

async function post(path: string, payload: unknown, signal?: AbortSignal) {
  const res = await fetch(`${config().gatewayBase}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(withHuman(path, payload)),
    ...(signal ? { signal } : {}),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  humanRefused(path, res.status, body);
  return { status: res.status, body };
}

const retryAfterOf = (body: Record<string, unknown>): number =>
  typeof body.retryAfter === "number" && body.retryAfter > 0 ? body.retryAfter : 60;

export type ResetProofOutcome =
  /** Proven: a new password may be set with this ticket. */
  | { kind: "proven"; resetTicket: string; rules: PasswordRules | null; expiresIn: number }
  /** The number belongs to more than one account: name yours. */
  | { kind: "username_needed"; ticket: string }
  | { kind: "username_mismatch" }
  | { kind: "no_account" }
  | { kind: "code_rejected" }
  | { kind: "busy"; retryAfter: number }
  | { kind: "rate_limited"; retryAfter: number }
  | { kind: "not_active" }
  | { kind: "expired" }
  | { kind: "unavailable"; status: number | null };

function proofOutcome(status: number, body: Record<string, unknown>): ResetProofOutcome {
  if (status === 200 && typeof body.resetTicket === "string" && body.resetTicket) {
    return {
      kind: "proven",
      resetTicket: body.resetTicket,
      rules: isRules(body.rules) ? body.rules : null,
      expiresIn: typeof body.expiresIn === "number" ? body.expiresIn : 600,
    };
  }
  if (status === 409 && body.error === "username_needed" && typeof body.ticket === "string" && body.ticket)
    return { kind: "username_needed", ticket: body.ticket };
  if (status === 401 && body.error === "username_mismatch") return { kind: "username_mismatch" };
  if (status === 401) return { kind: "code_rejected" };
  if (status === 404 && body.error === "no_active_account") return { kind: "no_account" };
  if (status === 403) return { kind: "not_active" };
  if (status === 429 && body.error === "verification_busy") return { kind: "busy", retryAfter: retryAfterOf(body) };
  if (status === 429) return { kind: "rate_limited", retryAfter: retryAfterOf(body) };
  if (status === 410 || status === 404) return { kind: "expired" };
  return { kind: "unavailable", status };
}

async function prove(path: string, payload: Record<string, unknown>, signal?: AbortSignal): Promise<ResetProofOutcome> {
  if (!AUTH_REQUEST_ID.test(String(payload.authRequestId))) return { kind: "expired" };
  try {
    const { status, body } = await post(path, payload, signal);
    return proofOutcome(status, body);
  } catch {
    return { kind: "unavailable", status: null };
  }
}

/** The SMS proof: the MSG91 widget's token for the typed mobile. */
export function forgotWithWidget(
  input: { authRequestId: string; mobile: string; accessToken: string; username?: string },
  signal?: AbortSignal,
): Promise<ResetProofOutcome> {
  const mobile = normalizeIndianMobile(input.mobile);
  if (mobile === null) return Promise.resolve({ kind: "unavailable", status: null });
  return prove("/auth/password/forgot/widget", {
    authRequestId: input.authRequestId, mobile, accessToken: input.accessToken, username: (input.username ?? "").trim(),
  }, signal);
}

/** A shared number: the username of the account to reset. */
export function forgotWithUsername(input: { authRequestId: string; ticket: string; username: string }, signal?: AbortSignal): Promise<ResetProofOutcome> {
  return prove("/auth/password/forgot/username", { ...input, username: input.username.trim() }, signal);
}

/** The email proof: the code from startEmail (login.ts). */
export function forgotWithEmailCode(input: { authRequestId: string; ticket: string; code: string }, signal?: AbortSignal): Promise<ResetProofOutcome> {
  return prove("/auth/password/forgot/email", { ...input, code: input.code.trim() }, signal);
}

export type ResetOutcome =
  | { kind: "reset" }
  /** The new password was the current one: nothing changed; sign in with it. */
  | { kind: "unchanged" }
  | { kind: "policy"; problem: string; rules: PasswordRules | null }
  | { kind: "rate_limited"; retryAfter: number }
  | { kind: "locked" }
  | { kind: "expired" }
  | { kind: "unavailable"; status: number | null };

export async function resetPassword(
  input: { authRequestId: string; resetTicket: string; newPassword: string },
  signal?: AbortSignal,
): Promise<ResetOutcome> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  let r;
  try {
    r = await post("/auth/password/reset", input, signal);
  } catch {
    return { kind: "unavailable", status: null };
  }
  const { status, body } = r;
  if (status === 200 && body.status === "reset") return { kind: "reset" };
  if (status === 200 && body.status === "unchanged") return { kind: "unchanged" };
  if (status === 422)
    return {
      kind: "policy",
      problem: typeof body.problem === "string" ? body.problem : "the password rules",
      rules: isRules(body.rules) ? body.rules : null,
    };
  if (status === 429) return { kind: "rate_limited", retryAfter: retryAfterOf(body) };
  if (status === 423) return { kind: "locked" };
  if (status === 410) return { kind: "expired" };
  return { kind: "unavailable", status };
}
